import React, { Fragment, useState, useEffect } from "react";
import { Link, useRouteMatch } from "react-router-dom";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { createProfile, getCurrentProfile } from "../../actions/profile";

const initialState = {
    company: "",
    website: "",
    location: "",
    status: "",
    skills: "",
    githubusername: "",
    bio: "",
    twitter: "",
    facebook: "",
    linkedin: "",
    youtube: "",
    instagram: "",
};

const ProfileForm = ({ profile: { profile, loading }, createProfile, getCurrentProfile, history, theme }) => {
    const [formData, setFormData] = useState(initialState);

    const creatingProfile = useRouteMatch("/create-profile");

    const [displaySocialInputs, toggleSocialInputs] = useState(false);

    useEffect(() => {
        if (!profile) getCurrentProfile();
        if (!loading && profile) {
            const profileData = { ...initialState };
            for (const key in profile) {
                if (key in profileData) profileData[key] = profile[key];
            }
            for (const key in profile.social) {
                if (key in profileData) profileData[key] = profile.social[key];
            }
            if (Array.isArray(profileData.skills)) profileData.skills = profileData.skills.join(", ");
            setFormData(profileData);
        }
    }, [loading, getCurrentProfile, profile]);

    const {
        company,
        website,
        location,
        status,
        skills,
        githubusername,
        bio,
        twitter,
        facebook,
        linkedin,
        youtube,
        instagram,
    } = formData;

    const onChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

    const onSubmit = (e) => {
        e.preventDefault();
        createProfile(formData, history, profile ? true : false);
    };

    return (
        <Fragment>
            <h3 className={`my-3 mb-3 mt-4 ${theme.brandText2}`}>
                {creatingProfile ? "Create Your Profile" : "Edit Your Profile"}
            </h3>
            <p className="lead">
                <i className="fas fa-user" />
                {creatingProfile ? ` Let's get some information to make your` : " Add some changes to your profile"}
            </p>
            <div>* = required field</div>
            <form className="form" onSubmit={onSubmit}>
                <div className="form-group">
                    <select
                        name="status"
                        value={status}
                        onChange={onChange}
                        className={`${theme.textWhite} ${theme.background1}`}
                    >
                        <option>* Select Professional Status</option>
                        <option value="Developer">Developer</option>
                        <option value="Junior Developer">Junior Developer</option>
                        <option value="Senior Developer">Senior Developer</option>
                        <option value="Manager">Manager</option>
                        <option value="Student or Learning">Student or Learning</option>
                        <option value="Instructor">Instructor or Teacher</option>
                        <option value="Intern">Intern</option>
                        <option value="Other">Other</option>
                    </select>
                    <small className="form-text">Give us an idea of where you are at in your career</small>
                </div>
                <div className="form-group">
                    <input
                        className={`${theme.textWhite}`}
                        type="text"
                        placeholder="Company"
                        name="company"
                        value={company}
                        onChange={onChange}
                    />
                    <small className="form-text">Could be your own company or one you work for</small>
                </div>
                <div className="form-group">
                    <input
                        className={`${theme.textWhite}`}
                        type="text"
                        placeholder="Website"
                        name="website"
                        value={website}
                        onChange={onChange}
                    />
                    <small className="form-text">Could be your own or a company website</small>
                </div>
                <div className="form-group">
                    <input
                        className={`${theme.textWhite}`}
                        type="text"
                        placeholder="Location"
                        name="location"
                        value={location}
                        onChange={onChange}
                    />
                    <small className="form-text">City & state suggested (eg. Boston, MA)</small>
                </div>
                <div className="form-group">
                    <input
                        className={`${theme.textWhite}`}
                        type="text"
                        placeholder="* Skills"
                        name="skills"
                        value={skills}
                        onChange={onChange}
                    />
                    <small className="form-text">Please use comma separated values (eg. HTML,CSS,JavaScript,PHP)</small>
                </div>
                <div className="form-group">
                    <input
                        className={`${theme.textWhite}`}
                        type="text"
                        placeholder="Github Username"
                        name="githubusername"
                        value={githubusername}
                        onChange={onChange}
                    />
                    <small className="form-text">
                        If you want your latest repos and a Github link, include your username
                    </small>
                </div>
                <div className="form-group input-field">
                    <textarea
                        placeholder="A short bio of yourself"
                        name="bio"
                        value={bio}
                        onChange={onChange}
                        className={`${theme.textWhite} materialize-textarea`}
                    />
                    <label for="textarea1"></label>
                    <small className="form-text">Tell us a little about yourself</small>
                </div>

                <div className="my-2">
                    <button
                        onClick={() => toggleSocialInputs(!displaySocialInputs)}
                        type="button"
                        className={`btn my-1 mr-2 ${theme.primaryVariant}`}
                    >
                        Add Social Network Links
                    </button>
                    <span>* Optional</span>
                </div>

                {displaySocialInputs && (
                    <Fragment>
                        <div className="form-group social-input">
                            <i className="fab fa-twitter fa-2x" />
                            <input
                                className={`${theme.textWhite}`}
                                type="text"
                                placeholder="Twitter URL"
                                name="twitter"
                                value={twitter}
                                onChange={onChange}
                            />
                        </div>

                        <div className="form-group social-input">
                            <i className="fab fa-facebook fa-2x" />
                            <input
                                className={`${theme.textWhite}`}
                                type="text"
                                placeholder="Facebook URL"
                                name="facebook"
                                value={facebook}
                                onChange={onChange}
                            />
                        </div>

                        <div className="form-group social-input">
                            <i className="fab fa-youtube fa-2x" />
                            <input
                                className={`${theme.textWhite}`}
                                type="text"
                                placeholder="YouTube URL"
                                name="youtube"
                                value={youtube}
                                onChange={onChange}
                            />
                        </div>

                        <div className="form-group social-input">
                            <i className="fab fa-linkedin fa-2x" />
                            <input
                                className={`${theme.textWhite}`}
                                type="text"
                                placeholder="Linkedin URL"
                                name="linkedin"
                                value={linkedin}
                                onChange={onChange}
                            />
                        </div>

                        <div className="form-group social-input">
                            <i className="fab fa-instagram fa-2x" />
                            <input
                                className={`${theme.textWhite}`}
                                type="text"
                                placeholder="Instagram URL"
                                name="instagram"
                                value={instagram}
                                onChange={onChange}
                            />
                        </div>
                    </Fragment>
                )}

                <input type="submit" className={`btn my-1 mr-2 ${theme.primary} ${theme.textWhite}`} />
                <Link className={`btn my-1 mr-2 ${theme.primaryVariant}`} to="/dashboard">
                    Go Back
                </Link>
            </form>
        </Fragment>
    );
};

ProfileForm.propTypes = {
    createProfile: PropTypes.func.isRequired,
    getCurrentProfile: PropTypes.func.isRequired,
    profile: PropTypes.object.isRequired,
    theme: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
    profile: state.profile,
    theme: state.theme,
});

export default connect(mapStateToProps, { createProfile, getCurrentProfile })(ProfileForm);
